import React from 'react';
import { motion } from 'framer-motion';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { 
  Search, MessageSquare, Users, MapPin, CalendarDays, Trophy,
  Bell, Shield, Zap, Globe, Heart, Star, UserPlus, Building2,
  Target, Handshake, Clock, Award
} from 'lucide-react';

const FeaturesPage = () => {
  const mainFeatures = [
    {
      icon: Search,
      title: 'Smart Discovery',
      description: 'Find players, crew, clubs, and venues in your area with AI-powered search filters. Get matched based on your skill level, sport preferences, and availability.',
      color: 'from-primary/20 to-primary/5',
    },
    {
      icon: MessageSquare,
      title: 'Card-Based Posts',
      description: 'Post what you\'re looking for with our unique card system. Whether you need a cricket partner or a football venue, create a card and let others find you.',
      color: 'from-secondary/20 to-secondary/5',
    },
    {
      icon: Users,
      title: 'Community Building',
      description: 'Build your sports network by connecting with like-minded enthusiasts. Join groups, follow players, and stay updated with your sports community.',
      color: 'from-accent/20 to-accent/5',
    },
    {
      icon: MapPin,
      title: 'Location Intelligence',
      description: 'Discover opportunities near you with precise location filtering. Find venues, players, and events by city, locality, or within a specific radius.',
      color: 'from-primary/20 to-primary/5',
    },
    {
      icon: CalendarDays,
      title: 'Event Management',
      description: 'Create, manage, and join sports events effortlessly. Set up matches, tournaments, or practice sessions with automated scheduling and reminders.',
      color: 'from-secondary/20 to-secondary/5',
    },
    {
      icon: Trophy,
      title: 'Challenges & Competitions',
      description: 'Challenge other clubs and players to friendly matches. Organize tournaments, track results, and build your competitive reputation.',
      color: 'from-accent/20 to-accent/5',
    },
  ];

  const playerFeatures = [
    {
      icon: UserPlus,
      title: 'Find Playing Partners',
      description: 'Never play alone. Find players matching your skill level and schedule.',
    },
    {
      icon: Target,
      title: 'Skill Matching',
      description: 'Get matched with players of similar skill levels for better games.',
    },
    {
      icon: Bell,
      title: 'Instant Notifications',
      description: 'Get notified when players near you are looking for a game.',
    },
    {
      icon: Star,
      title: 'Player Ratings',
      description: 'Build your reputation through ratings and reviews from other players.',
    },
  ];

  const crewFeatures = [
    {
      icon: Award,
      title: 'Professional Profile',
      description: 'Showcase your credentials, experience, and specializations.',
    },
    {
      icon: Handshake,
      title: 'Direct Bookings',
      description: 'Receive booking requests directly from players and clubs.',
    },
    {
      icon: Clock,
      title: 'Availability Management',
      description: 'Set your schedule and let clients book available slots.',
    },
    {
      icon: Globe,
      title: 'Expand Your Reach',
      description: 'Connect with clients across multiple cities and sports.',
    },
  ];

  const clubFeatures = [
    {
      icon: Users,
      title: 'Team Management',
      description: 'Add and manage club members, assign roles, and track attendance.',
    },
    {
      icon: Trophy,
      title: 'Challenge System',
      description: 'Challenge other clubs for friendly matches or tournaments.',
    },
    {
      icon: Building2,
      title: 'Venue Partnerships',
      description: 'Find and partner with venues for regular practice and matches.',
    },
    {
      icon: Zap,
      title: 'Emergency Players',
      description: 'Find last-minute players when your regulars can\'t make it.',
    },
  ];

  const venueFeatures = [
    {
      icon: Globe,
      title: 'Online Presence',
      description: 'Get discovered by thousands of players and clubs searching for venues.',
    },
    {
      icon: CalendarDays,
      title: 'Booking Management',
      description: 'Manage all your bookings, slots, and availability in one place.',
    },
    {
      icon: Heart,
      title: 'Customer Reviews',
      description: 'Build trust with verified reviews from players and clubs.',
    },
    {
      icon: Shield,
      title: 'Secure Payments',
      description: 'Accept payments securely through the platform.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const FeatureCard = ({ icon: Icon, title, description, color }) => (
    <motion.div
      variants={itemVariants}
      className={`feature-card p-8 group hover:-translate-y-2 transition-all duration-300 bg-gradient-to-br ${color || 'from-card to-card'}`}
    >
      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
        <Icon className="w-7 h-7 text-primary" />
      </div>
      <h3 className="font-heading text-xl font-bold text-white mb-3">{title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
    </motion.div>
  );

  const SmallFeatureCard = ({ icon: Icon, title, description }) => (
    <motion.div
      variants={itemVariants}
      className="flex gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
    >
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h4 className="font-semibold text-white mb-1">{title}</h4>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">
              Powerful Features
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6">
              Everything You Need to <span className="gradient-text">Play Better</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Discover the features that make Playmatez the ultimate sports networking platform 
              for players, crews, clubs, and venues across India.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Features Grid */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {mainFeatures.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* For Players Section */}
      <section className="py-20 bg-card/30">
        <div className="container-custom">
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">
              For Players
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Find Your Perfect Match
            </h2>
            <p className="text-muted-foreground max-w-xl">
              Whether you're looking for a badminton partner or a full cricket team, 
              Playmatez helps you connect with the right players.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {playerFeatures.map((feature) => (
              <SmallFeatureCard key={feature.title} {...feature} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* For Crew Section */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            className="mb-12 text-right"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">
              For Crew
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Grow Your Sports Career
            </h2>
            <p className="text-muted-foreground max-w-xl ml-auto">
              Coaches, referees, analysts, and sports professionals can showcase their 
              expertise and connect with clients directly.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {crewFeatures.map((feature) => (
              <SmallFeatureCard key={feature.title} {...feature} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* For Clubs Section */}
      <section className="py-20 bg-card/30">
        <div className="container-custom">
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">
              For Clubs
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Manage & Compete
            </h2>
            <p className="text-muted-foreground max-w-xl">
              From local sports clubs to professional teams, manage your members, 
              find opponents, and organize matches seamlessly.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {clubFeatures.map((feature) => (
              <SmallFeatureCard key={feature.title} {...feature} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* For Venues Section */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            className="mb-12 text-right"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">
              For Venues
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Fill Your Courts & Fields
            </h2>
            <p className="text-muted-foreground max-w-xl ml-auto">
              Sports venue owners can list their facilities, manage bookings, 
              and connect with a growing community of sports enthusiasts.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {venueFeatures.map((feature) => (
              <SmallFeatureCard key={feature.title} {...feature} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-secondary/10" />
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
              Download Playmatez now and join thousands of sports enthusiasts across India.
            </p>
            <motion.a
              href="https://download.playmatez.io/u/F4AE8rel"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              data-testid="features-cta-btn"
            >
              Download Now
            </motion.a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeaturesPage;
